// Tiny dependency-free colour helpers used to derive a full brand ramp from the tenant's
// single brandPrimaryColor. Everything guards against null/invalid hex and falls back to
// the caller-supplied default, so a missing or malformed colour never throws.

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Parse "#rgb", "#rrggbb" or "#rrggbbaa" (alpha ignored) into 0–255 channels, or null. */
export function hexToRgb(hex: string | null | undefined): Rgb | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6);
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

export function rgbToHex({ r, g, b }: Rgb): string {
  const to2 = (n: number) => clamp(n).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** Linearly blend `hex` toward `target` by `amount` (0..1). */
export function mix(hex: string, target: Rgb, amount: number): string {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const a = Math.max(0, Math.min(1, amount));
  return rgbToHex({
    r: c.r + (target.r - c.r) * a,
    g: c.g + (target.g - c.g) * a,
    b: c.b + (target.b - c.b) * a,
  });
}

/** Darken toward black. */
export const shade = (hex: string, amount: number) => mix(hex, { r: 0, g: 0, b: 0 }, amount);

/** Lighten toward white. */
export const tint = (hex: string, amount: number) => mix(hex, { r: 255, g: 255, b: 255 }, amount);

/** Relative luminance (WCAG sRGB -> linear), 0 (black) .. 1 (white). */
export function luminance(hex: string): number {
  const c = hexToRgb(hex);
  if (!c) return 0;
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/** A readable ink colour to place ON the given background: white for dark colours, dark for light ones. */
export function readableOn(hex: string): string {
  return luminance(hex) < 0.55 ? "#ffffff" : "#101317";
}

/** rgba(...) string from a hex + alpha (0..1). */
export function withAlpha(hex: string, alpha: number): string {
  const c = hexToRgb(hex);
  if (!c) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
}
